import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import LanguageSelector from '../i18n/LanguageSelector';
import './Navbar.css';
import './ShareholderNavbar.css';

const PAGE_ICONS = {
  dashboard: 'dashboard', decisions: 'decisions', meetings: 'calendar',
  users: 'user', permissions: 'lock', ownership: 'money', dividends: 'chart', settings: 'settings',
};

const PAGE_TITLE_KEYS = {
  dashboard:   'common.dashboard',
  decisions:   'common.decisions',
  meetings:    'common.myMeetings',
  users:       'common.users',
  permissions: 'common.permissions',
  ownership:   'nav.myOwnership',
  dividends:   'nav.dividends',
  settings:    'common.settings',
};

const DEMO_OWNERSHIP_PCT = '18.5';

export default function ShareholderNavbar({ activePage, onToggleSidebar }) {
  const { language } = useLanguage();
  const t = useT();
  const navigate = useNavigate();

  const metaIcon  = PAGE_ICONS[activePage]    ?? PAGE_ICONS.dashboard;
  const pageTitle = t(PAGE_TITLE_KEYS[activePage] ?? PAGE_TITLE_KEYS.dashboard);

  const todayStr = useMemo(() => new Date().toLocaleDateString(
    language === 'ar' || language === 'ur' ? 'ar-SA-u-ca-gregory'
      : language === 'zh' ? 'zh-CN' : language === 'fr' ? 'fr-FR' : 'en-US',
    { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
  ), [language]);

  const [dropOpen, setDropOpen] = useState(false);
  const [searchVal, setSearchVal] = useState('');
  const dropRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const dropItems = useMemo(() => ([
    { icon: 'user',     color: '#02c3a6', labelKey: 'common.editProfile',     to: '/shareholder/settings'  },
    { icon: 'money',    color: '#02c3a6', labelKey: 'nav.myOwnership',        to: '/shareholder/ownership' },
    { icon: 'settings', color: '#546e7a', labelKey: 'common.accountSettings', to: '/shareholder/settings'  },
  ]), []);

  return (
    <header className="navbar shareholder-navbar">
      <div className="navbar__accent" />

      <button type="button" className="navbar__hamburger" onClick={onToggleSidebar} aria-label={t('common.openMenu')}>
        <span /><span /><span />
      </button>

      <div className="navbar__identity">
        <div className="navbar__page-badge">
          <Icon name={metaIcon} size={17} color="#fff" strokeWidth={1.8} />
        </div>
        <div className="navbar__identity-text">
          <span className="navbar__title">{pageTitle}</span>
          <div className="navbar__breadcrumb">
            <span>{t('common.brand')}</span>
            <Icon name="chevron-left" size={9} color="#cfd8dc" />
            <span>{pageTitle}</span>
          </div>
        </div>
      </div>

      <div className={`navbar__search${searchVal ? ' navbar__search--filled' : ''}`}>
        <Icon name="search" size={14} color="#90a4ae" className="navbar__search-icon" />
        <input
          className="navbar__search-input"
          type="text"
          placeholder={t('common.quickSearch')}
          value={searchVal}
          onChange={(e) => setSearchVal(e.target.value)}
        />
        {searchVal
          ? <button type="button" className="navbar__search-clear" onClick={() => setSearchVal('')}>✕</button>
          : <span className="navbar__search-hint">⌘K</span>
        }
      </div>

      <div className="navbar__controls">

        <div className="sh-navbar__ownership">
          <Icon name="money" size={13} color="#02c3a6" />
          <span>{t('nav.myStake')} <strong>{DEMO_OWNERSHIP_PCT}%</strong></span>
        </div>

        <div className="navbar__date-chip sh-navbar__date">
          <Icon name="calendar" size={13} color="#02c3a6" />
          <span>{todayStr}</span>
        </div>

        <div className="navbar__sep" />

        <button type="button" className="navbar__ctrl-btn" title={t('common.notifications')}>
          <Icon name="bell" size={17} color="#546e7a" />
          <span className="navbar__badge">2</span>
        </button>

        <LanguageSelector variant="icon" placement="bottom-end" />

        <div className="navbar__sep" />

        <div className="navbar__user-wrap" ref={dropRef}>
          <button type="button" className={`navbar__user${dropOpen ? ' navbar__user--open' : ''}`}
            onClick={() => setDropOpen((o) => !o)}>
            <div className="navbar__avatar-ring">
              <div className="navbar__avatar sh-avatar">SH</div>
            </div>
            <div className="navbar__user-info">
              <span className="navbar__user-name">{t('nav.leadShareholder')}</span>
              <span className="navbar__user-role">{t('common.brand')} · {DEMO_OWNERSHIP_PCT}%</span>
            </div>
            <Icon name={dropOpen ? 'chevron-up' : 'chevron-down'} size={10} color="#90a4ae" />
          </button>

          {dropOpen && (
            <div className="navbar__dropdown">
              <div className="navbar__drop-header">
                <div className="navbar__drop-avatar sh-drop-avatar">SH</div>
                <div>
                  <div className="navbar__drop-name">{t('nav.leadShareholder')}</div>
                  <div className="navbar__drop-role">{t('nav.activeShareholder')} · {t('common.brand')}</div>
                </div>
              </div>
              <div className="navbar__drop-body">
                {dropItems.map((it) => (
                  <button key={it.labelKey} type="button" className="navbar__drop-item"
                    onClick={() => { setDropOpen(false); navigate(it.to); }}>
                    <span className="navbar__drop-item-icon">
                      <Icon name={it.icon} size={14} color={it.color} />
                    </span>
                    {t(it.labelKey)}
                  </button>
                ))}
                <div className="navbar__drop-sep" />
                <button type="button" className="navbar__drop-item navbar__drop-item--danger"
                  onClick={() => { setDropOpen(false); navigate('/login'); }}>
                  <span className="navbar__drop-item-icon">
                    <Icon name="log-out" size={14} color="#c62828" />
                  </span>
                  {t('common.logout')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
